import { useTranslation } from "@redotech/redo-i18n";
import { PickupData } from "@redotech/redo-return-operations-common/draft-return/draft-return";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import { useCallback, useState } from "react";
import { getValidDate } from "../../shipment-method/util";
import { MAX_DAYS } from "./use-package-pickup";

export const useDatePickerState = ({
  today,
  minDays,
  minDate: minDatePlain,
  updatePickupInfo,
}: {
  today: Temporal.PlainDate;
  minDays: number;
  minDate: Temporal.PlainDate;
  updatePickupInfo: (updates: Partial<PickupData>) => void;
}): {
  error?: string;
  maxDate: Date;
  minDate: Date;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  handleDateChange: (date?: Date) => void;
} => {
  const { t } = useTranslation();
  const maxDatePlain = getValidDate(today, MAX_DAYS);

  // Convert Temporal.PlainDate to Date
  const minDate = plainDateToCurrentDate(minDatePlain);
  const maxDate = plainDateToCurrentDate(maxDatePlain);

  // Initialize selectedDate with the minDate (today for normal returns,
  // a week from today for returns that need manual review)
  const [selectedDate, setSelectedDate] = useState<Date>(
    plainDateToCurrentDate(minDatePlain),
  );

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerError, setDatePickerError] = useState<string | undefined>(
    undefined,
  );

  const handleDateChange = useCallback(
    (date?: Date) => {
      if (!date) {
        setDatePickerError(t("Date is required"));
        return;
      }

      const plainDate = dateToCurrentPlainDate(date);
      const isLessThanMin =
        Temporal.PlainDate.compare(plainDate, minDatePlain) < 0;
      const isGreaterThanMax =
        Temporal.PlainDate.compare(plainDate, maxDatePlain) > 0;

      if (isLessThanMin || isGreaterThanMax) {
        setDatePickerError(
          t("Pickup date must be between {{minDays}} and 14 days from today", {
            minDays,
          }),
        );
        return;
      }

      // Check if the selected date is a weekend (Saturday = 6, Sunday = 7)
      const dayOfWeek = plainDate.dayOfWeek;
      if (dayOfWeek === 6 || dayOfWeek === 7) {
        setDatePickerError(t("Pickup date must be on a weekday"));
        return;
      }

      setDatePickerError(undefined);
      setSelectedDate(date);
      updatePickupInfo({ pickupDate: plainDate.toJSON() });
    },
    [minDatePlain, maxDatePlain, updatePickupInfo, minDays, t],
  );

  return {
    error: datePickerError,
    maxDate,
    minDate,
    selectedDate,
    setSelectedDate,
    open: datePickerOpen,
    setOpen: setDatePickerOpen,
    setError: setDatePickerError,
    handleDateChange,
  };
};
